import React, { useState } from 'react'
import { ChevronLeftIcon, ChevronRightIcon, PlusIcon } from 'lucide-react'
import { ContractJobTemplate } from '../api/contractJobTemplates'

interface Job {
  id: string
  job_number: string
  title: string
  scheduled_date?: string
  status: string
  priority?: string
}

interface ContractJobsCalendarProps {
  jobs: Job[]
  templates: ContractJobTemplate[]
  projectedDates?: string[]
  onJobClick: (job: Job) => void
  onTemplateClick: (template: ContractJobTemplate) => void
  onDateClick: (date: Date) => void
}

export function ContractJobsCalendar({
  jobs,
  templates,
  projectedDates = [],
  onJobClick,
  onTemplateClick,
  onDateClick,
}: ContractJobsCalendarProps) {
  const [currentDate, setCurrentDate] = useState(new Date())

  const year = currentDate.getFullYear()
  const month = currentDate.getMonth()

  // Get first day of month and number of days
  const firstDay = new Date(year, month, 1)
  const lastDay = new Date(year, month + 1, 0)
  const daysInMonth = lastDay.getDate()
  const startingDayOfWeek = firstDay.getDay()

  // Get month name
  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ]

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

  // Navigate months
  const goToPreviousMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1))
  }

  const goToNextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1))
  }

  const goToToday = () => {
    setCurrentDate(new Date())
  }

  // Get jobs/templates for a specific date
  const getItemsForDate = (date: Date) => {
    const dateStr = date.toISOString().split('T')[0]
    const dateJobs = jobs.filter((job) => job.scheduled_date === dateStr)
    const dateTemplates = templates.filter(
      (template) => template.scheduled_date === dateStr && !template.job_id
    )
    const isProjected = projectedDates.includes(dateStr)
    return { jobs: dateJobs, templates: dateTemplates, isProjected }
  }

  // Check if date is today
  const isToday = (date: Date) => {
    const today = new Date()
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    )
  }

  // Check if date is in the past
  const isPast = (date: Date) => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const checkDate = new Date(date)
    checkDate.setHours(0, 0, 0, 0)
    return checkDate < today
  }

  // Render calendar grid
  const renderCalendarDays = () => {
    const days = []
    const today = new Date()

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(
        <div key={`empty-${i}`} className="h-32 border border-gray-200 bg-gray-50"></div>
      )
    }

    // Add cells for each day of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day)
      const items = getItemsForDate(date)
      const isCurrentDay = isToday(date)
      const isPastDate = isPast(date)

      days.push(
        <div
          key={day}
          className={`h-32 border border-gray-200 p-2 ${
            isCurrentDay ? 'bg-blue-50 border-blue-300' : isPastDate ? 'bg-gray-50' : 'bg-white'
          } hover:bg-gray-50 transition-colors cursor-pointer relative`}
          onClick={() => onDateClick(date)}
        >
          <div className="flex items-center justify-between mb-1">
            <span
              className={`text-sm font-medium ${
                isCurrentDay ? 'text-blue-600' : isPastDate ? 'text-gray-400' : 'text-gray-900'
              }`}
            >
              {day}
            </span>
            {isCurrentDay && (
              <span className="text-xs bg-blue-600 text-white px-1.5 py-0.5 rounded">Today</span>
            )}
          </div>

          <div className="space-y-1 overflow-y-auto max-h-20">
            {/* Created Jobs */}
            {items.jobs.map((job) => (
              <div
                key={job.id}
                onClick={(e) => {
                  e.stopPropagation()
                  onJobClick(job)
                }}
                className={`text-xs p-1 rounded truncate ${
                  job.status === 'completed'
                    ? 'bg-green-100 text-green-800 border border-green-200'
                    : job.status === 'in_progress'
                    ? 'bg-blue-100 text-blue-800 border border-blue-200'
                    : job.status === 'scheduled'
                    ? 'bg-yellow-100 text-yellow-800 border border-yellow-200'
                    : 'bg-gray-100 text-gray-800 border border-gray-200'
                } hover:opacity-80 cursor-pointer`}
                title={`${job.job_number}: ${job.title}`}
              >
                #{job.job_number}
              </div>
            ))}

            {/* Scheduled Templates (not yet created) */}
            {items.templates.map((template) => (
              <div
                key={template.id}
                onClick={(e) => {
                  e.stopPropagation()
                  onTemplateClick(template)
                }}
                className="text-xs p-1 rounded truncate bg-purple-100 text-purple-800 border border-purple-200 hover:opacity-80 cursor-pointer"
                title={template.title || `Scheduled: ${template.scheduled_date}`}
              >
                {template.title ? template.title.substring(0, 15) : 'Scheduled'}
              </div>
            ))}

            {/* Projected recurring date (in cycle but not yet materialized) */}
            {items.jobs.length === 0 && items.templates.length === 0 && items.isProjected && (
              <div
                className="text-xs p-1 rounded truncate bg-indigo-50 text-indigo-700 border border-indigo-200"
                title="Projected recurring date (click day to add details)"
              >
                Projected
              </div>
            )}

            {/* Add new template button */}
            {items.jobs.length === 0 && items.templates.length === 0 && !items.isProjected && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onDateClick(date)
                }}
                className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1"
                title="Add scheduled job"
              >
                <PlusIcon className="w-3 h-3" />
                Add
              </button>
            )}
          </div>
        </div>
      )
    }

    return days
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      {/* Calendar Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={goToPreviousMonth}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronLeftIcon className="w-5 h-5 text-gray-600" />
          </button>
          <h3 className="text-xl font-semibold text-gray-900">
            {monthNames[month]} {year}
          </h3>
          <button
            onClick={goToNextMonth}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronRightIcon className="w-5 h-5 text-gray-600" />
          </button>
        </div>
        <button
          onClick={goToToday}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
        >
          Today
        </button>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 mb-4 text-xs">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-yellow-100 border border-yellow-200 rounded"></div>
          <span className="text-gray-600">Scheduled</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-blue-100 border border-blue-200 rounded"></div>
          <span className="text-gray-600">In Progress</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-green-100 border border-green-200 rounded"></div>
          <span className="text-gray-600">Completed</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-purple-100 border border-purple-200 rounded"></div>
          <span className="text-gray-600">Scheduled (Not Created)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-indigo-50 border border-indigo-200 rounded"></div>
          <span className="text-gray-600">Projected Recurring</span>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-0 border border-gray-200 rounded-lg overflow-hidden">
        {/* Day Headers */}
        {dayNames.map((day) => (
          <div
            key={day}
            className="bg-gray-100 p-2 text-center text-sm font-medium text-gray-700 border-b border-gray-200"
          >
            {day}
          </div>
        ))}

        {/* Calendar Days */}
        {renderCalendarDays()}
      </div>
    </div>
  )
}

